'use client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts, type FetchProductQuery } from '@eliadmin/core/src/api/products/product-api';
import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { FAVORITES_COPY } from '@eliadmin/core/src/components/dlz/account/favorites/favorites-copy';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { isSellable, normalizeProductQuantity } from '@eliadmin/core/src/utils/product-utils';

/** Figma „Zobraziť ďalšie 2“ under six cards: the grid grows by six. */
export const FAVORITES_STEP = 6;
/** One request holds the usual list; longer lists are read page by page. */
const PAGE_SIZE = 100;

const PLURAL = new Intl.PluralRules('sk');

/** Slovak plural form of a count: 1 · 2–4 · other. */
function pluralForm(count: number): 'one' | 'few' | 'other' {
  const form = PLURAL.select(count);
  return form === 'one' || form === 'few' ? form : 'other';
}

/** „1 produkt“, „2 produkty“, „8 produktov“. */
export function productCount(count: number) {
  return `${count} ${FAVORITES_COPY.products[pluralForm(count)]}`;
}

/** Figma „Price alert“: „Na 2 obľúbené produkty klesla cena. Upozornenia … e-mailom.“ */
export function priceAlertText(count: number) {
  const form = pluralForm(count);
  const { priceAlert, products } = FAVORITES_COPY;
  return priceAlert.text(`${count} ${priceAlert.favorites[form]} ${products[form]}`);
}

type ApiSort = NonNullable<FetchProductQuery['sort']>;

/**
 * Sort options of the „Zoradiť“ menu. „Naposledy pridané“ is the order the API returns favourites
 * in (no explicit sort); the others are API sorts of the product listing.
 */
export const FAVORITES_SORTS = [
  { value: 'recent', label: FAVORITES_COPY.sort.recent },
  { value: 'cheapest', textKey: 'text177', sort: 'price' },
  { value: 'priciest', textKey: 'text178', sort: '-price' },
] as const satisfies readonly { value: string; label?: string; textKey?: string; sort?: ApiSort }[];

export type FavoritesSort = (typeof FAVORITES_SORTS)[number]['value'];

function apiSort(sort: FavoritesSort): ApiSort | undefined {
  const option = FAVORITES_SORTS.find(item => item.value === sort);
  return option && 'sort' in option ? option.sort : undefined;
}

/** Every favourite of the customer, all pages of the listing. */
export async function fetchAllFavorites({
  locale,
  authToken,
  sort = 'recent',
  signal,
}: {
  locale: string;
  authToken?: string;
  sort?: FavoritesSort;
  signal?: AbortSignal;
}) {
  const products: Product[] = [];
  for (let page = 1; ; page++) {
    const response = await fetchProducts({
      locale,
      authToken,
      signal,
      query: { favorite: true, sort: apiSort(sort), page, results: PAGE_SIZE },
    });
    products.push(...response.data);
    if (page >= (response.meta?.last_page ?? page)) return products;
  }
}

/**
 * The whole list, so the count, the discount alert and „Pridať všetko do košíka“ see every
 * favourite; the grid reveals it in steps. The favourite button invalidates `FAVORITE_PRODUCTS`, so
 * removing a favourite refetches the list.
 */
export function useFavoriteProducts(sort: FavoritesSort) {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);

  return useQuery({
    queryKey: [CacheKey.FAVORITE_PRODUCTS, 'account', locale, authToken ?? null, sort],
    queryFn: ({ signal }) => fetchAllFavorites({ locale, authToken, sort, signal }),
    enabled: !!authToken,
    // Another sort keeps the current cards on screen until the new order arrives.
    placeholderData: keepPreviousData,
  });
}

/** Favourites shown with the old price crossed out – the same rule as the sale badge of the card. */
export function countDiscounted(products: Product[]) {
  return products.filter(product => getProductPriceDisplay(product).showPreviousPrice).length;
}

export type CartReadiness = 'ready' | 'variant' | 'unavailable';

/**
 * Whether a favourite can go to the cart without a choice – the rules of the core cart button:
 * a product with variants needs one picked, a product on request, ended, sold by an affiliate or
 * out of stock cannot be bought.
 */
export function cartReadiness(product: Product): CartReadiness {
  if (product.has_variants || product.variants?.length) return 'variant';
  const supplier = [...(product.suppliers ?? [])].sort((a, b) => a.position - b.position)[0];
  const quantity = normalizeProductQuantity(1, product);
  const buyable =
    product.status !== 'ended' &&
    !supplier?.is_affiliate &&
    !getProductPriceDisplay(product).priceOnRequest &&
    isSellable(product, quantity);
  return buyable ? 'ready' : 'unavailable';
}
